<template>

    <div id="form-container" ref="formContainer">
        <b-container fluid>
            <h1>Register Account</h1>
            <b-row>
                <b-col>
                    <p>
                        Please enter the following personal and contact information.
                    </p>
                </b-col>
            </b-row>

            <b-form class="mt-3">
                <h2>
                    Personal Information
                </h2>
                <b-row>
                    <b-col cols="4">
                        <BaseTextInput v-model="profile.firstName"
                                       :v="$v.profile.firstName"
                                       slug="firstName"
                                       label="First Name"
                        />
                    </b-col>
                    <b-col cols="4">
                        <BaseTextInput v-model="profile.middleName"
                                       :v="$v.profile.middleName"
                                       slug="middleName"
                                       label="Middle Name"
                        />
                    </b-col>
                    <b-col cols="4">
                        <BaseTextInput v-model="profile.lastName"
                                       :v="$v.profile.lastName"
                                       slug="lastName"
                                       label="Last Name"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="12" lg="6">
                        <BaseDateOfBirthInput v-model="profile.dateOfBirth"
                                              :v="$v.profile.dateOfBirth"
                                              label="Date of Birth"
                                              :messages="dobMessages"
                                              slug="dob"
                                              setId="dob"
                                              tip="Format: MM/DD/YYYY"
                        />
                    </b-col>
                    <b-col cols="12" lg="6">
                        <BaseMaskedTextInput v-model="profile.ssn"
                                             :v="$v.profile.ssn"
                                             slug="ssn"
                                             label="Social Security Number"
                                             setId="ssn"
                                             mask="###-##-####"
                        />
                    </b-col>
                </b-row>

                <h2>Contact Information</h2>
                <b-row>
                    <b-col cols="6">
                        <BaseMaskedTextInput v-model="profile.contact.phone"
                                             :v="$v.profile.contact.phone"
                                             slug="phone"
                                             label="Cell Phone Number"
                                             setId="phone"
                                             mask="(###)###-####"
                        />
                    </b-col>
                    <b-col cols="6">
                        <BaseTextInput v-model="profile.contact.email"
                                       :v="$v.profile.contact.email"
                                       slug="email"
                                       label="Email"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <BaseSelector v-model="profile.textingPreferences"
                                      :v="$v.profile.textingPreferences"
                                      slug="textingPreference"
                                      label="Texting Preferences"
                                      :options="opts.textingPreferenceOptions"/>
                    </b-col>
                </b-row>
                <h3>
                    Address:
                </h3>
                <b-row>
                    <b-col cols="8">
                        <BaseTextInput v-model="profile.contact.address.streetAddress"
                                       :v="$v.profile.contact.address.streetAddress"
                                       slug="streetAddress"
                                       label="Street Address"
                                       tip="Please include apartment number or PO Box number if relevant."
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="4">
                        <BaseTextInput v-model="profile.contact.address.city"
                                       :v="$v.profile.contact.address.city"
                                       slug="city"
                                       label="City"
                        />
                    </b-col>
                    <b-col cols="4">
                        <BaseSelector v-model="profile.contact.address.state"
                                      :v="$v.profile.contact.address.state"
                                      label="State"
                                      slug="state"
                                      :options=opts.stateOptions
                        />
                    </b-col>
                    <b-col cols="4">
                        <BaseMaskedTextInput v-model="profile.contact.address.postalCode"
                                             :v="$v.profile.contact.address.postalCode"
                                             slug="postalCode"
                                             label="ZIP"
                                             mask="#####-####"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-button type="button" variant="primary" @click="register">Register</b-button>
                    </b-col>
                </b-row>
            </b-form>
        </b-container>

    </div>
</template>

<script>
import axios from "axios"
import axiosRetry from 'axios-retry'
import minDate from '@/validators/minDate'
import maxDate from '@/validators/maxDate'
import { required, minLength, maxLength, email } from 'vuelidate/lib/validators'
import {toastService} from "@/mixins/toastService";
import {commonOptions} from "@/mixins/commonOptions";
import {registerService} from "@/common/RegisterService";

axiosRetry(axios, { retries: 0,
    retryDelay: axiosRetry.exponentialDelay  })

export default {
    data() {
        return {
            opts:{
                textingPreferenceOptions: []
            },
            profile: {
                ssn: null,
                dateOfBirth: null,
                lastName: null,
                firstName: null,
                middleName: null,
                contact: {
                    phone: null,
                    email: null,
                    address: {
                        streetAddress: null,
                        city: null,
                        state: null,
                        postalCode: null,
                    }
                },
                textingPreference: null
            },
            dobMessages: {
                maxDate: 'Date of Birth cannot be in the future.',
                minDate: 'Date of Birth must be after 01/01/1900.'
            },
        }
    },
    mixins: [
        toastService,
        commonOptions
    ],
    validations() {
        return {
            profile: {
                ssn: {
                    required: required,
                    minLength: minLength(11),
                    maxLength: maxLength(11)
                },
                dateOfBirth: {
                    required,
                    maxDate: maxDate(new Date()),
                    minDate: minDate(new Date('1900-01-01'))
                },
                contact: {
                    phone: { required,
                        minLength: minLength(13),
                        maxLength: maxLength(13) },
                    email: { required, email },
                    address: {
                        streetAddress: { required },
                        city: { required },
                        state: { required },
                        postalCode: { required,
                            maxLength: maxLength(10),
                            minLength: minLength(5) },
                    },
                },
                firstName: { required },
                lastName: { required },
                middleName: { required: false },
                textingPreferences: {required}
            }
        }
    },
    methods: {
        register() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                this.$nextTick(() => {
                    document.getElementById('form-container').scrollIntoView()
                })
                return
            }
            axios.post(`${process.env.VUE_APP_API_BASE_URL}/profile/register`,
                this.profile)
                .then(response => {
                    if(response.status === 200) {
                        toastService.showRootToast("Success", "You have registered your account.", "success", this.$root)
                        registerService.refreshToken(300)
                            .then((t) => { setTimeout( () => this.$router.push({ path: '/home'}), 10000); })
                    }
                })
                .catch(error => {
                  if(error.response.status===404){
                    toastService.showRootToast("Not found", "No certificate matching this name, ssn, and date of birth found. Please check the information and try again. If you are certain that the information provided is correct, please contact MDH for support.", "danger", this.$root)
                  }
                  else{
                    toastService.showRootToast("Error", "An error has occurred while processing your request. Please log out and contact MDH for further instructions.", "danger", this.$root)
                  }
                })
        },
        loadTextingOptions(){
            axios.get(`${process.env.VUE_APP_API_BASE_URL}/profile/texting/getList`)
                .then(response=>{
                    for(let option of response.data){
                        this.opts.textingPreferenceOptions.push({
                            value: option.code,
                            text: option.description
                        })
                    }
                })
        },
    },
    created() {
        document.title = "Register Account - NAR"
        this.loadTextingOptions();
    }
}
</script>